/**
 * Minimal name + email signup modal for guest favourites.
 */

import {
	addFavouriteAfterAuth,
	celebrateFavouriteForProduct,
	onFavouritesAuthSuccess,
} from './favourites.js';
import { focusWithoutScroll, lockBodyScroll, unlockBodyScroll } from './modal-scroll-lock.js';

let modalEl = null;
let pendingProductId = 0;
let previouslyFocused = null;

function getConfig() {
	return window.doneCommerce || {};
}

function escapeHtml(value) {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function ensureModal() {
	if (modalEl) {
		return modalEl;
	}

	const config = getConfig();
	const loginUrl = config.accountUrl || '/my-account/';
	const title = config.i18n?.saveToFavourites || 'Save to favourites';
	const intro =
		config.i18n?.favouritesSignupIntro ||
		'Create a free account with your name and email to save products.';
	const nameLabel = config.i18n?.yourName || 'Name';
	const emailLabel = config.i18n?.yourEmail || 'Email';
	const submitLabel = config.i18n?.createAccount || 'Create account';
	const loginPrompt = config.i18n?.alreadyHaveAccount || 'Already have an account?';
	const loginLink = config.i18n?.logIn || 'Log in';
	const closeLabel = config.i18n?.close || 'Close';

	modalEl = document.createElement('div');
	modalEl.className = 'dc-auth-modal';
	modalEl.hidden = true;
	modalEl.innerHTML = `
		<div class="dc-modal__backdrop" data-auth-modal-close></div>
		<div
			class="dc-modal__panel dc-auth-modal__panel"
			role="dialog"
			aria-modal="true"
			aria-labelledby="dc-auth-modal-title"
			tabindex="-1"
			data-auth-modal-panel
		>
			<button type="button" class="dc-auth-modal__close" data-auth-modal-close aria-label="${escapeHtml(closeLabel)}">
				<span aria-hidden="true">&times;</span>
			</button>
			<h2 id="dc-auth-modal-title" class="dc-auth-modal__title">${escapeHtml(title)}</h2>
			<p class="dc-auth-modal__intro">${escapeHtml(intro)}</p>
			<form class="dc-auth-modal__form" data-auth-modal-form novalidate>
				<p class="dc-auth-modal__error" data-auth-modal-error hidden></p>
				<label class="dc-auth-modal__field">
					<span>${escapeHtml(nameLabel)}</span>
					<input type="text" name="name" autocomplete="name" required maxlength="100" data-auth-name>
				</label>
				<label class="dc-auth-modal__field">
					<span>${escapeHtml(emailLabel)}</span>
					<input type="email" name="email" autocomplete="email" required maxlength="200" data-auth-email>
				</label>
				<button type="submit" class="dc-btn dc-btn--primary dc-auth-modal__submit" data-auth-submit>
					${escapeHtml(submitLabel)}
				</button>
			</form>
			<p class="dc-auth-modal__login">
				${escapeHtml(loginPrompt)}
				<a href="${escapeHtml(loginUrl)}" data-auth-login-link>${escapeHtml(loginLink)}</a>
			</p>
		</div>
	`;

	document.body.appendChild(modalEl);

	modalEl.addEventListener('click', (e) => {
		if (e.target.closest('[data-auth-modal-close]')) {
			closeAuthModal();
		}
	});

	modalEl.querySelector('[data-auth-modal-form]')?.addEventListener('submit', onSubmit);

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && modalEl && !modalEl.hidden) {
			closeAuthModal();
		}
	});

	return modalEl;
}

function setError(message, loginUrl) {
	const errorEl = modalEl?.querySelector('[data-auth-modal-error]');
	const loginLink = modalEl?.querySelector('[data-auth-login-link]');
	if (!errorEl) {
		return;
	}

	if (!message) {
		errorEl.hidden = true;
		errorEl.textContent = '';
		return;
	}

	errorEl.hidden = false;
	errorEl.textContent = message;
	if (loginUrl && loginLink) {
		loginLink.href = loginUrl;
	}
}

function applyAuthSession(data) {
	const config = getConfig();
	config.isLoggedIn = true;
	if (data.nonce) {
		config.nonce = data.nonce;
	}
	if (data.first_name) {
		config.firstName = data.first_name;
	}
	window.doneCommerce = config;

	onFavouritesAuthSuccess(data.favourites || { ids: [] });
}

async function onSubmit(e) {
	e.preventDefault();
	const form = e.currentTarget;
	const name = form.querySelector('[data-auth-name]')?.value?.trim() || '';
	const email = form.querySelector('[data-auth-email]')?.value?.trim() || '';
	const submit = form.querySelector('[data-auth-submit]');
	const config = getConfig();

	setError('');

	if (name.length < 2) {
		setError(config.i18n?.invalidName || 'Please enter your name.');
		return;
	}

	if (!email.includes('@')) {
		setError(config.i18n?.invalidEmail || 'Please enter a valid email address.');
		return;
	}

	if (submit) {
		submit.disabled = true;
	}

	try {
		const body = { name, email };
		if (pendingProductId) {
			body.product_id = pendingProductId;
		}

		const response = await fetch(`${config.restUrl}done-commerce/v1/auth/quick-register`, {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': config.nonce,
			},
			body: JSON.stringify(body),
		});

		const data = await response.json().catch(() => ({}));

		if (!response.ok) {
			const message =
				data?.message ||
				config.i18n?.registerFailed ||
				'Unable to create account. Please try again.';
			const loginUrl = data?.data?.login_url || config.accountUrl;
			setError(message, loginUrl);
			return;
		}

		const productId = pendingProductId;
		closeAuthModal();
		applyAuthSession(data);

		if (productId && !(data.favourites?.ids || []).map(Number).includes(productId)) {
			await addFavouriteAfterAuth(productId);
		} else if (productId) {
			onFavouritesAuthSuccess(data.favourites);
			celebrateFavouriteForProduct(productId);
		}
	} catch {
		setError(config.i18n?.registerFailed || 'Unable to create account. Please try again.');
	} finally {
		if (submit) {
			submit.disabled = false;
		}
	}
}

export function openAuthModal({ productId = 0 } = {}) {
	pendingProductId = Number(productId) || 0;
	const modal = ensureModal();
	const panel = modal.querySelector('[data-auth-modal-panel]');
	const form = modal.querySelector('[data-auth-modal-form]');

	setError('');
	form?.reset();
	previouslyFocused = document.activeElement;
	modal.hidden = false;
	lockBodyScroll();
	focusWithoutScroll(panel);
	focusWithoutScroll(form?.querySelector('[data-auth-name]'));
}

export function closeAuthModal() {
	if (!modalEl || modalEl.hidden) {
		return;
	}

	modalEl.hidden = true;
	unlockBodyScroll();
	pendingProductId = 0;
	focusWithoutScroll(previouslyFocused);
	previouslyFocused = null;
}

export function initAuthModal() {
	document.addEventListener('dc:open-auth-modal', (e) => {
		const productId = Number(e.detail?.productId) || 0;
		openAuthModal({ productId });
	});
}
